import { OtkList } from '../../models/user-item-list';
import { Action, Selector, State, StateContext } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { patch } from '@ngxs/store/operators';
import { CloseDialog, CreateList, EditList, OpenDialog, UpdateLocalList } from './edit-list.actions';
import { EditListService } from '../services/edit-list.service';
import { tap, throwError } from 'rxjs';
import { UserService } from '../../services/user.service';
import { catchError, finalize } from 'rxjs/operators';
import { Record } from '../../models/record';
import { LoadingModel } from '../../_core/state/loading.model';
import { ToastService } from '../../_core/services/toast.service';
import { AnalyticsService } from '../../_core/analytics/analytics.service';

export type EditListStateModel = LoadingModel & {
  open: boolean;
  list?: OtkList;
  record?: Record;
};

export const initialState: EditListStateModel = {
  isLoading: false,
  open: false,
  list: undefined,
  record: undefined,
};

@State<EditListStateModel>({
  name: 'editList',
  defaults: initialState,
})
@Injectable()
export class EditListState {
  private readonly editListService = inject(EditListService);
  private readonly toastService = inject(ToastService);
  private readonly userService = inject(UserService);
  private readonly analyticsService = inject(AnalyticsService);

  @Selector()
  static open(state: EditListStateModel): boolean {
    return state.open;
  }

  @Selector()
  static list(state: EditListStateModel): OtkList | undefined {
    return state.list;
  }

  @Selector()
  static record(state: EditListStateModel): Record | undefined {
    return state.record;
  }

  @Action(OpenDialog)
  openDialog(context: StateContext<EditListStateModel>, { list, record }: OpenDialog) {
    context.setState(
      patch({
        open: true,
        list,
        record,
      })
    );
  }

  @Action(CloseDialog)
  closeDialog(context: StateContext<EditListStateModel>) {
    context.setState(
      patch({
        open: false,
        list: undefined,
        record: undefined,
      })
    );
  }

  @Action(UpdateLocalList)
  updateLocalList(context: StateContext<EditListStateModel>, { list }: UpdateLocalList) {
    context.setState(
      patch({
        list: patch({ ...list }),
      })
    );
  }

  @Action(CreateList)
  createList(context: StateContext<EditListStateModel>, { userId, list, record }: CreateList) {
    context.setState(patch({ isLoading: true }));

    return this.editListService.createList(userId, list, record).pipe(
      tap((createdList) => {
        this.userService.lists.unshift(createdList);
        this.toastService.showToast('editList.new');

        context.dispatch(new CloseDialog());

        this.analyticsService.sendListCreatedEvent(true);
      }),
      catchError((error) => {
        this.analyticsService.sendListCreatedEvent(false);
        return throwError(() => error);
      }),
      finalize(() => {
        context.setState(patch({ isLoading: false }));
      })
    );
  }

  @Action(EditList)
  editList(context: StateContext<EditListStateModel>, { list }: EditList) {
    context.setState(patch({ isLoading: true }));

    return this.editListService.editList(list).pipe(
      tap((updatedList) => {
        const cachedList = this.userService.lists.find(({ id }) => id === updatedList.id);

        if (cachedList) {
          cachedList.name = updatedList.name;
          cachedList.description = updatedList.description;
          cachedList.selectedCoverIdx = updatedList.selectedCoverIdx;
          cachedList.privateList = updatedList.privateList;
          cachedList.customCoverUrl = updatedList.customCoverUrl;

          if (updatedList.privateList) {
            cachedList.followers = 0;
          }
        }

        this.toastService.showToast('editList.edit');

        context.dispatch(new CloseDialog());

        this.analyticsService.sendUserActionEvent('edit_list', true);
      }),
      catchError((error) => {
        this.analyticsService.sendUserActionEvent('edit_list', false);
        return throwError(() => error);
      }),
      finalize(() => {
        context.setState(patch({ isLoading: false }));
      })
    );
  }
}
